import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, computed, signal } from '@angular/core';
import { IconDirective, Icon } from '@seech/shared-ng';

export type AvatarShape = 'circle' | 'square' | 'rounded';
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'custom';

export interface AvatarConfig {
  name?: string;
  imageUrl?: string;
  size?: AvatarSize | string;
  shape?: AvatarShape;
  backgroundColor?: string;
  textColor?: string;
  fontSize?: string;
  borderWidth?: string;
  borderColor?: string;
  shadow?: boolean;
  clickable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  fallbackText?: string;
  showInitials?: boolean;
  maxInitials?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
}

@Component({
  selector: 'seech-avatar',
  standalone: true,
  imports: [CommonModule, IconDirective],
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
})
export class AvatarComponent implements OnInit, OnChanges {
  // Input properties with defaults
  @Input() name = '';
  @Input() imageUrl: string | null = null;
  @Input() size: AvatarSize | string = 'md';
  @Input() shape: AvatarShape = 'circle';
  @Input() backgroundColor: string | null = null;
  @Input() textColor: string | null = null;
  @Input() fontSize: string | null = null;
  @Input() borderWidth = '0px';
  @Input() borderColor = '#e5e7eb';
  @Input() shadow = false;
  @Input() clickable = false;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() placeholder = '?';
  @Input() fallbackText: string | null = null;
  @Input() showInitials = true;
  @Input() maxInitials = 2;
  @Input() objectFit: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none' = 'cover';
  @Input() overlayIcon: Icon | null = null;
  @Input() overlayIconSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'auto' = 'auto';
  @Input() overlayIconColor = '#ffffff';
  @Input() overlayIconBackground = '#007bff';
  @Input() overlayIconPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' = 'bottom-right';
  @Input() overlayIconAutoRatio = 3.5; // avatar diameter divided by overlay diameter when auto sizing

  @Output() overlayIconClick = new EventEmitter<MouseEvent>();

  // Internal state
  showImage = signal(true);
  backgroundColorComputed = signal('');
  textColorComputed = signal('');

  // Computed properties
  initials = computed(() => {
    if (!this.showInitials || !this.name) return '';

    const words = this.name.trim().split(/\s+/);
    const initials = words
      .slice(0, this.maxInitials)
      .map(word => word.charAt(0).toUpperCase())
      .join('');

    return initials || this.placeholder;
  });

  displayText = computed(() => {
    if (this.fallbackText) return this.fallbackText;
    return this.initials();
  });

  effectiveSize = computed(() => {
    if (typeof this.size === 'string' && this.size.startsWith('custom')) {
      return this.size.replace('custom:', '');
    }
    return this.getSizeValue(this.size as AvatarSize);
  });

  effectiveShape = computed(() => {
    switch (this.shape) {
      case 'circle':
        return '50%';
      case 'square':
        return '0';
      case 'rounded':
        return '8px';
      default:
        return '50%';
    }
  });

  effectiveFontSize = computed(() => {
    if (this.fontSize) return this.fontSize;

    const sizeValue = this.effectiveSize();
    const numericSize = parseFloat(sizeValue);
    return `${numericSize * 0.4}px`;
  });

  overlayIconSizeValue = computed(() => {
    if (this.overlayIconSize === 'auto') {
      const avatarSize = parseFloat(this.effectiveSize());
      if (!Number.isFinite(avatarSize) || avatarSize <= 0) {
        return '16px';
      }

      const ratio = this.overlayIconAutoRatio > 0 ? this.overlayIconAutoRatio : 6.5;
      const overlayDiameter = avatarSize / ratio;
      const glyphScale = 0.58; // keeps glyph comfortably inside the overlay badge
      return `${+(overlayDiameter * glyphScale).toFixed(2)}px`;
    }

    const sizeMap: Record<string, string> = {
      xs: '12px',
      sm: '16px',
      md: '20px',
      lg: '24px',
      xl: '28px',
    };
    return sizeMap[this.overlayIconSize] || '16px';
  });

  overlayContainerSizeValue = computed(() => {
    const avatarSize = parseFloat(this.effectiveSize());
    const glyphSize = parseFloat(this.overlayIconSizeValue());

    if (!Number.isFinite(glyphSize) || glyphSize <= 0) {
      return '32px';
    }

    if (this.overlayIconSize === 'auto' && Number.isFinite(avatarSize) && avatarSize > 0) {
      const ratio = this.overlayIconAutoRatio > 0 ? this.overlayIconAutoRatio : 6.5;
      return `${+(avatarSize / ratio).toFixed(2)}px`;
    }

    const scale = 1.8;
    const minPadding = 8;
    const scaledSize = glyphSize * scale;
    const minSize = glyphSize + minPadding;
    const avatarLimit = Number.isFinite(avatarSize) && avatarSize > 0 ? avatarSize * 0.65 : scaledSize;
    const preferred = Math.min(scaledSize, avatarLimit);
    return `${+Math.max(preferred, minSize).toFixed(2)}px`;
  });

  overlayPositionOffsets = computed(() => {
    const avatarSize = parseFloat(this.effectiveSize());

    if (!Number.isFinite(avatarSize) || avatarSize <= 0) {
      return { x: '0px', y: '0px' };
    }

    const radius = avatarSize / 2;
    const isCircular = this.effectiveShape() === '50%';
    const baseOffset = isCircular ? radius / Math.SQRT2 : radius;
    const horizontal = this.overlayIconPosition.includes('left') ? -1 : 1;
    const vertical = this.overlayIconPosition.includes('top') ? -1 : 1;

    return {
      x: `${+(baseOffset * horizontal).toFixed(2)}px`,
      y: `${+(baseOffset * vertical).toFixed(2)}px`,
    };
  });

  overlayIconPositionClass = computed(() => {
    return `seech-avatar__overlay--${this.overlayIconPosition}`;
  });

  ngOnInit(): void {
    this.setColors();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['backgroundColor'] || changes['name']) {
      this.setColors();
    }
  }

  onImageError(): void {
    this.showImage.set(false);
  }

  onImageLoad(): void {
    this.showImage.set(true);
  }

  onClick(): void {
    if (this.clickable && !this.disabled && !this.loading) {
      console.log('Avatar clicked');
    }
  }

  onOverlayIconClick(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.disabled && !this.loading) {
      this.overlayIconClick.emit(event);
    }
  }

  private setColors(): void {
    this.backgroundColorComputed.set(
      this.backgroundColor || this.generateBackgroundColor()
    );
    this.textColorComputed.set(
      this.textColor || this.getContrastColor(this.backgroundColorComputed())
    );
  }

  private generateBackgroundColor(): string {
    if (!this.name) return '#6b7280';

    let hash = 0;
    for (let i = 0; i < this.name.length; i++) {
      hash = this.name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    const saturation = 60 + (Math.abs(hash) % 20);
    const lightness = 50 + (Math.abs(hash) % 20);

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  private getContrastColor(backgroundColor: string): string {
    if (backgroundColor.startsWith('hsl(')) {
      const hslMatch = backgroundColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
      if (hslMatch) {
        const lightness = parseInt(hslMatch[3], 10);
        return lightness > 50 ? '#000000' : '#ffffff';
      }
    }

    if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#000000' : '#ffffff';
    }

    return '#ffffff';
  }

  private getSizeValue(size: AvatarSize): string {
    const sizeMap: Record<AvatarSize, string> = {
      xs: '24px',
      sm: '32px',
      md: '40px',
      lg: '48px',
      xl: '64px',
      '2xl': '80px',
      '3xl': '96px',
      '4xl': '128px',
      custom: '',
    };

    return sizeMap[size] || '40px';
  }
}